import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api"; // keep your existing api helper

function fmtMoney(x, digits = 4) {
    const n = Number(x || 0);
    if (Number.isNaN(n)) return "0.0000";
    return n.toFixed(digits);
}

function daysUntil(dateStr) {
    if (!dateStr) return null;
    const d = new Date(`${dateStr}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return null;

    const now = new Date();
    const utcNow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const diffMs = d.getTime() - utcNow.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function pillClass(tone = "neutral") {
    switch (tone) {
        case "warn":
            return "bg-black/30 border-white/10 text-white";
        case "good":
            return "bg-white/10 border-white/10 text-white";
        default:
            return "bg-black/30 border-white/10 text-gray-200";
    }
}

export default function DividendTracker() {
    const [next, setNext] = useState(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [symbol, setSymbol] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // NEW: include OTC toggle (default OFF)
    const [includeOTC, setIncludeOTC] = useState(false);

    // NEW: company names map
    const [names, setNames] = useState({}); // {SYM: "Company Name"}

    // Trade UI
    const [tradeQtyBySymbol, setTradeQtyBySymbol] = useState({});
    const [trading, setTrading] = useState({}); // {SYM: true}
    const [tradeMsg, setTradeMsg] = useState("");

    function getUserId() {
        return (
            localStorage.getItem("user_id") ||
            localStorage.getItem("email") ||
            localStorage.getItem("userEmail") ||
            ""
        );
    }

    async function loadNamesForSymbols(symbols) {
        const uniq = Array.from(new Set((symbols || []).filter(Boolean).map((s) => String(s).toUpperCase())));
        if (uniq.length === 0) return;

        // avoid re-fetching ones we already have
        const missing = uniq.filter((s) => !names[s]);
        if (missing.length === 0) return;

        try {
            const res = await api.post("/api/symbols/names", { tickers: missing });
            const map = res?.data || {};
            setNames((prev) => ({ ...prev, ...map }));
        } catch {
            // non-fatal
        }
    }

    async function loadAll(p = page) {
        setLoading(true);
        setError("");
        try {
            const paramsBase = {
                page: p,
                limit: 50,
                include_otc: includeOTC ? 1 : 0,
                symbol: symbol || undefined,
            };

            const [nres, lres] = await Promise.all([
                api.get("/api/dividends/next", { params: { include_otc: includeOTC ? 1 : 0, ...(symbol ? { symbol } : {}) } }),
                api.get("/api/dividends/upcoming", { params: paramsBase }),
            ]);

            const n = nres.data?.next || null;
            const list = lres.data?.results || [];

            setNext(n);
            setRows(list);
            setPages(lres.data?.pages || 1);
            setPage(lres.data?.page || p);

            // fetch names for "next" + page symbols
            const syms = [
                ...(n?.symbol ? [n.symbol] : []),
                ...list.map((r) => r.symbol),
            ];
            loadNamesForSymbols(syms);
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Failed to load dividends");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadAll(1);
        // eslint-disable-next-line
    }, []);

    // reload if OTC toggle changes
    useEffect(() => {
        loadAll(1);
        // eslint-disable-next-line
    }, [includeOTC]);

    async function tradeStock(row) {
        const sym = (row?.symbol || "").toUpperCase();
        if (!sym) return;

        const user_id = getUserId();
        if (!user_id) {
            setTradeMsg("Not signed in (missing user_id). Please re-login.");
            return;
        }

        const qty = Number(tradeQtyBySymbol[sym] || 1);
        if (!Number.isFinite(qty) || qty <= 0) {
            setTradeMsg("Quantity must be a positive number.");
            return;
        }

        setTrading((m) => ({ ...m, [sym]: true }));
        setTradeMsg("");

        try {
            await api.post("/api/trade/place", {
                user_id,
                symbol: sym,
                side: "buy",
                qty,
                type: "market",
                time_in_force: "day",
                source: "manual",
            });

            const holdUntil = row.record_date || row.ex_dividend_date || "";
            setTradeMsg(`Placed BUY for ${sym} (qty ${qty}). Hold target: ${holdUntil || "�"}`);
        } catch (e) {
            setTradeMsg(e?.response?.data?.error || e?.message || `Trade failed for ${sym}`);
        } finally {
            setTrading((m) => ({ ...m, [sym]: false }));
        }
    }

    const header = useMemo(() => {
        if (!next) return null;

        const sym = (next.symbol || "").toUpperCase();
        const name = names[sym] || "";
        const dte = daysUntil(next.ex_dividend_date);
        const dteLabel = dte === null ? "" : dte < 0 ? `(${Math.abs(dte)}d ago)` : `(in ${dte}d)`;

        return (
            <div className="bg-[#0b1220] border border-white/10 rounded-xl p-4 mb-4">
                <div className="text-sm text-gray-300">Up next</div>

                <div className="flex items-start justify-between gap-3 flex-wrap mt-1">
                    <div>
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-2xl font-bold text-yellow-300 animate-pulse">{sym}</span>
                            {name ? <span className="text-sm text-gray-300">{name}</span> : null}
                        </div>

                        <div className="text-gray-300 mt-1">
                            Ex-div:{" "}
                            <span className="text-white font-semibold">
                                {next.ex_dividend_date || "�"} {dteLabel}
                            </span>
                            <span className="mx-2 text-gray-600">-</span>
                            Per share:{" "}
                            <span className="text-white font-semibold">${fmtMoney(next.dividend_per_share, 4)}</span>
                        </div>

                        <div className="text-xs text-gray-400 mt-2">
                            Decl: {next.declaration_date || "�"} &nbsp; : &nbsp; Record: {next.record_date || "�"} &nbsp; : &nbsp; Pay:{" "}
                            {next.payment_date || "�"}
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [next, names]);

    function DividendCard({ r }) {
        const sym = (r.symbol || "").toUpperCase();
        const name = names[sym] || "";
        const dte = daysUntil(r.ex_dividend_date);

        const urgencyTone = dte !== null && dte <= 2 ? "warn" : "neutral";

        return (
            <div className="bg-[#0b1220] border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-lg font-bold text-yellow-300 animate-pulse">{sym}</span>
                            {name ? (
                                <span className="text-sm text-gray-300 truncate max-w-[260px]">{name}</span>
                            ) : null}
                        </div>

                        <div className="text-sm text-gray-300 mt-1">
                            <span className="text-white font-semibold">${fmtMoney(r.dividend_per_share, 4)}</span>{" "}
                            <span className="text-gray-500">/ share</span>
                            {r.currency ? <span className="text-gray-500"> - {r.currency}</span> : <span className="text-gray-500"> - USD</span>}
                        </div>
                    </div>

                    <div className="text-right shrink-0">
                        <div className="text-xs text-gray-400">Ex-div</div>
                        <div className="text-sm text-gray-200 whitespace-nowrap">
                            {r.ex_dividend_date || "�"}
                        </div>
                        <div className="text-xs text-gray-500">
                            {dte === null ? "" : dte < 0 ? `${Math.abs(dte)}d ago` : `in ${dte}d`}
                        </div>
                    </div>
                </div>

                {/* Pills */}
                <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full border ${pillClass("neutral")}`}>
                        Decl: {r.declaration_date || "�"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${pillClass(urgencyTone)}`}>
                        Ex-div: {r.ex_dividend_date || "�"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${pillClass("neutral")}`}>
                        Record: {r.record_date || "�"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${pillClass("neutral")}`}>
                        Pay: {r.payment_date || "�"}
                    </span>
                </div>

                {/* Trade */}
                <div className="mt-4 flex gap-2 items-center">
                    <input
                        className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm w-20"
                        value={tradeQtyBySymbol[sym] || ""}
                        placeholder="Qty"
                        inputMode="numeric"
                        onChange={(e) => setTradeQtyBySymbol((m) => ({ ...m, [sym]: e.target.value }))}
                        aria-label={`Qty for ${sym}`}
                    />

                    <button
                        className="bg-green-600 hover:bg-green-500 text-white rounded-lg px-3 py-2 text-sm flex-1 disabled:opacity-50"
                        onClick={() => tradeStock(r)}
                        disabled={!!trading[sym]}
                    >
                        {trading[sym] ? "Placing�" : "Trade Stock"}
                    </button>
                </div>

                <div className="text-xs text-gray-500 mt-2">
                    Tip: To capture the dividend, buy <b>before</b> the ex-div date and hold through that day.
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="text-xl font-bold">Dividend Tracker (NYSE)</div>
                    <div className="text-xs text-gray-400">Upcoming dividends + key dates</div>
                </div>

                <div className="flex gap-2 items-center flex-wrap">
                    <input
                        className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm w-44"
                        placeholder="Filter symbol (AAPL)"
                        value={symbol}
                        onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    />

                    <label className="flex items-center gap-2 text-xs text-gray-300 select-none">
                        <input
                            type="checkbox"
                            className="accent-blue-500"
                            checked={includeOTC}
                            onChange={(e) => setIncludeOTC(e.target.checked)}
                        />
                        Include OTC / non-NYSE
                    </label>

                    <button
                        className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-3 py-2 text-sm"
                        onClick={() => loadAll(1)}
                        disabled={loading}
                    >
                        {loading ? "Loading�" : "Refresh"}
                    </button>
                </div>
            </div>

            {error ? <div className="text-red-400 text-sm mb-3">{error}</div> : null}
            {tradeMsg ? <div className="text-emerald-300 text-sm mb-3">{tradeMsg}</div> : null}

            {header}

            {/* Cards grid (no overlap on mobile) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {rows.map((r, idx) => (
                    <DividendCard key={`${r.symbol}-${r.ex_dividend_date}-${idx}`} r={r} />
                ))}
            </div>

            {rows.length === 0 && !loading ? (
                <div className="mt-3 p-4 text-sm text-gray-400 bg-[#0b1220] border border-white/10 rounded-xl">
                    No upcoming dividends found (cache may need refresh).
                </div>
            ) : null}

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
                <button
                    className="px-3 py-2 rounded-lg border border-white/10 text-sm disabled:opacity-40"
                    onClick={() => loadAll(Math.max(1, page - 1))}
                    disabled={page <= 1 || loading}
                >
                    Prev
                </button>

                <div className="text-xs text-gray-400">
                    Page {page} / {pages}
                </div>

                <button
                    className="px-3 py-2 rounded-lg border border-white/10 text-sm disabled:opacity-40"
                    onClick={() => loadAll(Math.min(pages, page + 1))}
                    disabled={page >= pages || loading}
                >
                    Next
                </button>
            </div>
        </div>
    );
}
